import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Trash, X, type Icon } from '@phosphor-icons/react';
import { menuePosition } from '../../../../../components/immobilien/email/listenAnsicht';
import { wiedervorlagePresets } from '../../../../../lib/emails/wiedervorlage';
import { useImmoTheme } from '../../../../../lib/immobilien/theme';

export interface MenueAktion { icon: Icon; label: string; onClick: () => void; aktivFarbe?: string }

const PANEL: React.CSSProperties = {
  position: 'fixed', zIndex: 410, borderRadius: 18, padding: 6, display: 'flex', flexDirection: 'column',
  background: 'var(--bg)', color: 'var(--ink)', boxShadow: '0 24px 50px -16px var(--shade), inset 0 0 0 1px var(--line)',
  fontFamily: "'Manrope', -apple-system, sans-serif",
};

/** Rechtsklick-Menü einer Mail (Einträge wie bisher, „Löschen“ abgesetzt). Schließen übernimmt EmailListe (Klick, Scroll, Esc). */
export function EmailKontextmenue({ x, y, aktionen, onLoeschen }: { x: number; y: number; aktionen: MenueAktion[]; onLoeschen: () => void }) {
  const pos = menuePosition(x, y, window.innerWidth, window.innerHeight, 250, 60 + aktionen.length * 40);
  return createPortal(
    <div data-email-modal role="menu" aria-label="E-Mail-Aktionen" className="immo-side-in" style={{ ...PANEL, ...pos, width: 240 }} onClick={e => e.stopPropagation()}>
      {aktionen.map(a => (
        <button key={a.label} type="button" role="menuitem" onClick={a.onClick} className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
          style={{ gap: 10, minHeight: 38, padding: '0 10px', borderRadius: 11, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: 600 }}>
          <a.icon size={16} weight={a.aktivFarbe ? 'fill' : 'regular'} color={a.aktivFarbe ?? 'var(--ink2)'} />{a.label}
        </button>
      ))}
      <div style={{ height: 1, background: 'var(--line)', margin: '4px 6px' }} />
      <button type="button" role="menuitem" onClick={onLoeschen} className="flex items-center w-full text-left cursor-pointer"
        style={{ gap: 10, minHeight: 38, padding: '0 10px', borderRadius: 11, border: 'none', background: 'transparent', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bad-soft)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
        <Trash size={16} />Löschen
      </button>
    </div>,
    document.body,
  );
}

// datetime-local liefert lokale Zeit ohne Zone — als lokale Date interpretieren.
const lokalZuIso = (v: string): string | null => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Wiedervorlage-Wahl an der Klickposition (Prototyp „Wiedervorlage“): drei Presets, freie Zeit + OK, Aufheben.
 * Gleiche Presets und Prüfung wie der frühere WiedervorlagePicker (lib/emails/wiedervorlage).
 */
export function WiedervorlageWahl({ x, y, aktuellesDatum, onWahl, onAufheben, onSchliessen }: {
  x: number; y: number; aktuellesDatum?: string | null;
  onWahl: (iso: string) => void; onAufheben: () => void; onSchliessen: () => void;
}) {
  const [frei, setFrei] = useState('');
  const [theme] = useImmoTheme();
  const pos = menuePosition(x, y, window.innerWidth, window.innerHeight, 270, 280);
  const ok = !!frei && !!lokalZuIso(frei) && new Date(frei).getTime() > Date.now();
  return createPortal(
    <>
      <div className="fixed inset-0" style={{ zIndex: 409, background: 'rgba(10,18,30,0.15)' }} onClick={onSchliessen} />
      <div data-email-modal role="dialog" aria-label="Wiedervorlage" className="immo-side-in" style={{ ...PANEL, ...pos, width: 260, padding: 12, gap: 6 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center">
          <span className="flex-1 flex items-center" style={{ gap: 6, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
            <Clock size={12} weight="bold" />Wiedervorlage
          </span>
          <button type="button" aria-label="Schließen" title="Schließen" onClick={onSchliessen} className="flex items-center justify-center cursor-pointer"
            style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
            <X size={12} weight="bold" />
          </button>
        </div>
        {wiedervorlagePresets(new Date()).map(p => (
          <button key={p.label} type="button" onClick={() => onWahl(p.datum.toISOString())} className="immo-hover-flaeche text-left cursor-pointer"
            style={{ minHeight: 38, padding: '0 10px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, background: 'var(--fill)', color: 'var(--ink)' }}>
            {p.label}
          </button>
        ))}
        <div className="flex" style={{ gap: 6 }}>
          <input type="datetime-local" value={frei} onChange={e => setFrei(e.target.value)} aria-label="Datum und Uhrzeit"
            className="flex-1 min-w-0" style={{ height: 36, padding: '0 10px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 12, fontWeight: 600, color: 'var(--ink)', colorScheme: theme }} />
          <button type="button" disabled={!ok} onClick={() => { const iso = lokalZuIso(frei); if (iso) onWahl(iso); }} className="cursor-pointer"
            style={{ height: 36, padding: '0 12px', borderRadius: 12, border: 'none', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 12, fontWeight: 800, opacity: ok ? 1 : 0.4 }}>
            OK
          </button>
        </div>
        {aktuellesDatum && (
          <button type="button" onClick={onAufheben} className="text-left cursor-pointer"
            style={{ minHeight: 34, padding: '0 10px', borderRadius: 10, border: 'none', background: 'transparent', fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>
            Wiedervorlage aufheben
          </button>
        )}
      </div>
    </>,
    document.body,
  );
}

/**
 * #527: Zuweisen an der Klickposition – Teams und Mitarbeiter mit ihrer Listenfarbe, Suche, „Zuweisung entfernen“.
 * Schreibt wie die Detailansicht emails.zustaendig_id/-typ.
 */
export function ZuweisenWahl({ x, y, teams, mitarbeiter, farben, aktuell, onWahl, onSchliessen }: {
  x: number; y: number;
  teams: { id: string; name: string }[];
  mitarbeiter: { id: string; full_name: string }[];
  farben: Map<string, { fg: string; bg: string }>;
  aktuell: string | null;
  onWahl: (wahl: { id: string; typ: 'team' | 'mitarbeiter' } | null) => void;
  onSchliessen: () => void;
}) {
  const [suche, setSuche] = useState('');
  const pos = menuePosition(x, y, window.innerWidth, window.innerHeight, 270, 420);
  const q = suche.trim().toLowerCase();
  const passt = (n: string) => !q || n.toLowerCase().includes(q);
  const zeile = (id: string, name: string, typ: 'team' | 'mitarbeiter') => {
    const f = farben.get(id);
    return (
      <button key={id} type="button" role="menuitemradio" aria-checked={aktuell === id} onClick={() => onWahl({ id, typ })}
        className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
        style={{ gap: 9, minHeight: 36, padding: '0 10px', borderRadius: 10, border: 'none', background: aktuell === id ? 'var(--fill2)' : 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: aktuell === id ? 800 : 600 }}>
        <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: f?.fg ?? 'var(--ink3)' }} />
        <span className="flex-1 truncate">{name}</span>
        {typ === 'team' && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--ink3)' }}>Team</span>}
      </button>
    );
  };
  const t = teams.filter(x2 => passt(x2.name));
  const m = mitarbeiter.filter(x2 => passt(x2.full_name));
  return createPortal(
    <>
      <div className="fixed inset-0" style={{ zIndex: 409, background: 'rgba(10,18,30,0.15)' }} onClick={onSchliessen} />
      <div data-email-modal role="menu" aria-label="Zuweisen" className="immo-side-in" style={{ ...PANEL, ...pos, width: 260, padding: 8, gap: 4, maxHeight: 420 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center" style={{ padding: '2px 4px' }}>
          <span className="flex-1" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Zuweisen an</span>
          <button type="button" aria-label="Schließen" onClick={onSchliessen} className="flex items-center justify-center cursor-pointer"
            style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}><X size={12} weight="bold" /></button>
        </div>
        <input autoFocus value={suche} onChange={e => setSuche(e.target.value)} placeholder="Team oder Name …" aria-label="Team oder Mitarbeiter suchen"
          style={{ height: 34, padding: '0 10px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontFamily: 'inherit' }} />
        <div className="immo-scroll flex flex-col overflow-y-auto" style={{ gap: 2, maxHeight: 300 }}>
          {t.length > 0 && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--ink3)', padding: '6px 10px 2px' }}>TEAMS</span>}
          {t.map(x2 => zeile(x2.id, x2.name, 'team'))}
          {m.length > 0 && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--ink3)', padding: '6px 10px 2px' }}>MITARBEITER</span>}
          {m.map(x2 => zeile(x2.id, x2.full_name, 'mitarbeiter'))}
          {t.length + m.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink3)', padding: '8px 10px' }}>Nichts gefunden.</span>}
        </div>
        {aktuell && (
          <button type="button" onClick={() => onWahl(null)} className="text-left cursor-pointer"
            style={{ minHeight: 34, padding: '0 10px', borderRadius: 10, border: 'none', background: 'transparent', fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>
            Zuweisung entfernen
          </button>
        )}
      </div>
    </>,
    document.body,
  );
}
